import {
  HStack,
  VStack,
  Text,
  Image,
  Box,
} from '@chakra-ui/react';
import { MapPinIcon } from 'lucide-react';
import { Restaurant, SharedNote } from '@/types/restaurant';
import { getCompatibilityEmoji, getCompatibilityColor, hasValidAddressParts } from '@/utils';
import { TasteMatchPill } from '@/components/display/TasteMatchPill';
import { getRestaurantPillLabelProps } from '@/components/display/tasteMatchPillLabels';
import { useAppSelector } from '@/store/store';
import { selectCurrentUserId } from '@/store/slices/authSlice';

interface RestaurantItemCardProps {
  restaurant: Restaurant;
  showRating?: boolean;
  imageSize?: string | Record<string, string>;
  isMarkedForDeletion?: boolean;
  children?: React.ReactNode; // For additional content like drag handles
  rightContent?: React.ReactNode; // For distance display or other content
}

export function RestaurantItemCard({
  restaurant,
  showRating = false,
  imageSize = '50px',
  isMarkedForDeletion = false,
  children,
  rightContent
}: RestaurantItemCardProps) {
  const currentUserId = useAppSelector(selectCurrentUserId);
  const borderColor = getCompatibilityColor(restaurant);

  return (
    <VStack align="start" spacing={2} w="100%">
      {/* Top row: image + name/address */}
      <HStack spacing={2} align="start" w="100%">
        {children}
        {restaurant.coverPhoto && (
          <Box position="relative" flexShrink={0}>
            <Image
              h={imageSize}
              w={imageSize}
              objectFit="cover"
              borderRadius="var(--r-inner)"
              src={restaurant.coverPhoto}
            />
            {showRating && (
              <TasteMatchPill
                emojiSrc={getCompatibilityEmoji(restaurant).icon}
                emojiAlt={getCompatibilityEmoji(restaurant).label}
                tierColor={borderColor}
                {...getRestaurantPillLabelProps(restaurant)}
                positionProps={{ top: { base: 1, md: 2 }, right: 1 }}
              />
            )}
          </Box>
        )}
        <VStack align="start" spacing={1} flex={1}>
          <HStack justify="space-between" align="start" w="100%">
            <Text
              color="var(--card-text)"
              fontWeight="medium"
              minH="2.8em"
              noOfLines={2}
              textDecoration={isMarkedForDeletion ? "line-through" : "none"}
            >
              {restaurant.name}
            </Text>
            {rightContent}
          </HStack>
          <HStack spacing={1} align="start">
            <MapPinIcon size={14} color="var(--color-gray-500)" style={{ marginTop: 2, flexShrink: 0 }} />
            <Box
              color="var(--card-text-2)"
              fontSize="sm"
              minH="3.6em"
              textDecoration={isMarkedForDeletion ? "line-through" : "none"}
            >
              {hasValidAddressParts(restaurant.addressParts) ? (
                <>
                  <Text color="var(--card-text-2)">{restaurant.addressParts.address1}</Text>
                  <Text>{restaurant.addressParts.address2}</Text>
                  <Text>{restaurant.addressParts.address3}</Text>
                </>
              ) : (
                <Text noOfLines={3}>{restaurant.address}</Text>
              )}
            </Box>
          </HStack>
        </VStack>
      </HStack>

      {/* Full-width description */}
      {restaurant.description && (
        <Text
          color="var(--card-text-2)"
          fontSize="sm"
          noOfLines={3}
          textDecoration={isMarkedForDeletion ? "line-through" : "none"}
        >
          {restaurant.description}
        </Text>
      )}

      {/* Full-width notes */}
      {restaurant.sharedNotes && restaurant.sharedNotes.length > 0 && (
        <Box>
          {restaurant.sharedNotes.map((note: SharedNote, index: number) => (
            <Text
              key={index}
              color="var(--card-text-2)"
              fontSize="sm"
              fontStyle="italic"
              noOfLines={3}
            >
              <Text as="span" fontWeight="medium" fontStyle="normal">
                {note.user.id === currentUserId ? 'Notes:' : `${note.user.firstName} says:`}
              </Text>
              {' '}
              {note.notes}
            </Text>
          ))}
        </Box>
      )}
    </VStack>
  );
}